import {
  buildComputedLineItem,
  formatInvoiceAmountInput,
  resolveLineItemPayableAmount,
  resolveSavedLineTaxRate,
  resolveSavedLineTaxType,
} from "./invoiceLineItemHelpers";

export function normalizeInvoicePaymentLineItems(lineItems = []) {
  return (Array.isArray(lineItems) ? lineItems : []).map((line, index) => ({
    id: line.id ?? line.finance_invoice_item_id ?? line.line_item_id ?? null,
    finance_invoice_item_id:
      line.finance_invoice_item_id ?? line.id ?? line.line_item_id ?? null,
    line_number: Number(line.line_number ?? line.lineNumber) || index + 1,
    description: String(
      line.description || line.item_name || line.name || "",
    ).trim(),
    item_name: line.item_name || line.description || "",
    quantity: line.quantity ?? line.qty ?? 1,
    unit_price: line.unit_price ?? line.unitPrice ?? "",
    discount: line.discount ?? line.discount_amount ?? line.discountAmount ?? 0,
    fee_type:
      line.fee_type ??
      line.feeType ??
      (Number(line.bonus_installment_id ?? line.bonusInstallmentId)
        ? "bonus"
        : Number(line.commission_installment_id ?? line.commissionInstallmentId)
          ? "tuition"
          : ""),
    amount:
      line.amount ??
      line.line_total ??
      line.lineTotal ??
      line.total ??
      line.net,
    line_total:
      line.line_total ??
      line.lineTotal ??
      line.amount ??
      line.total ??
      line.net,
    commission_installment_id:
      line.commission_installment_id ?? line.commissionInstallmentId ?? null,
    bonus_installment_id:
      line.bonus_installment_id ?? line.bonusInstallmentId ?? null,
    tax_type: resolveSavedLineTaxType(line),
    tax_rate: resolveSavedLineTaxRate(line),
    tax_amount: line.tax_amount ?? line.taxAmount ?? null,
  }));
}

/** Normalize + compute line tax/amount fields for payment allocation modals. */
export function prepareInvoicePaymentLineItems(lineItems = []) {
  return normalizeInvoicePaymentLineItems(lineItems).map((line) =>
    buildComputedLineItem(line),
  );
}

/**
 * Per-line amount due in the add-payment modal — matches invoice footer
 * (commission/bonus: qty × price + exclusive GST; inclusive tax in amount).
 */
export function resolvePaymentModalLineDueAmount(line = {}) {
  const computed = buildComputedLineItem(line);
  return Math.max(resolveLineItemPayableAmount(computed), 0);
}

export function buildFinancePaymentLineRows({
  lineItems = [],
  draftPayments = [],
  existingTransactions = [],
} = {}) {
  const paidByLine = new Map();
  const itemIdToLineNumber = new Map();
  const commissionIdToLineNumber = new Map();
  const bonusIdToLineNumber = new Map();

  (lineItems || []).forEach((line, index) => {
    const lineNumber = Number(line.line_number) || index + 1;
    const itemId = line.id ?? line.finance_invoice_item_id ?? line.line_item_id ?? null;
    if (itemId) {
      itemIdToLineNumber.set(Number(itemId), lineNumber);
    }

    const commissionId = Number(
      line.commission_installment_id ?? line.commissionInstallmentId,
    );
    if (commissionId > 0) {
      commissionIdToLineNumber.set(commissionId, lineNumber);
    }

    const bonusId = Number(line.bonus_installment_id ?? line.bonusInstallmentId);
    if (bonusId > 0) {
      bonusIdToLineNumber.set(bonusId, lineNumber);
    }
  });

  const addPaid = (lineNumber, amount) => {
    if (!lineNumber || !Number.isFinite(Number(amount)) || Number(amount) === 0) {
      return;
    }
    paidByLine.set(
      lineNumber,
      (paidByLine.get(lineNumber) || 0) + Number(amount),
    );
  };

  const resolveReversedPaymentId = (record = {}) => {
    const direct =
      record.reversed_payment_id ??
      record.reverses_entry_id ??
      record.reverses_transaction_id ??
      null;
    if (direct) return Number(direct);

    const reference = String(record.reference || "").trim();
    const match = reference.match(/^payment_reversal_(\d+)$/i);
    return match ? Number(match[1]) : null;
  };

  const resolveLineNumber = (payment = {}) => {
    const directLineNumber = Number(payment.line_number);
    if (directLineNumber > 0) return directLineNumber;

    const itemId = Number(payment.finance_invoice_item_id);
    if (itemId > 0 && itemIdToLineNumber.has(itemId)) {
      return itemIdToLineNumber.get(itemId);
    }

    const commissionId = Number(
      payment.commission_installment_id ?? payment.commissionInstallmentId,
    );
    if (commissionId > 0 && commissionIdToLineNumber.has(commissionId)) {
      return commissionIdToLineNumber.get(commissionId);
    }

    const bonusId = Number(
      payment.bonus_installment_id ?? payment.bonusInstallmentId,
    );
    if (bonusId > 0 && bonusIdToLineNumber.has(bonusId)) {
      return bonusIdToLineNumber.get(bonusId);
    }

    return null;
  };

  (draftPayments || []).forEach((payment) => {
    addPaid(resolveLineNumber(payment), payment.amount);
  });

  const paymentIdToLineNumber = new Map();
  let unlinkedNetPaid = 0;

  (existingTransactions || []).forEach((record) => {
    const type = String(record?.type || record?.transaction_type || "").toLowerCase();
    if (type !== "payment") return;

    const lineNumber = resolveLineNumber(record);
    const paymentId = Number(record.id);
    if (lineNumber && paymentId > 0) {
      paymentIdToLineNumber.set(paymentId, lineNumber);
    }
  });

  (existingTransactions || []).forEach((record) => {
    const type = String(record?.type || record?.transaction_type || "").toLowerCase();
    const amount = Math.abs(Number(record.amount) || 0);
    if (amount <= 0) return;

    if (type === "reverse_payment" || type === "reversal" || type === "payment_reversal") {
      const reversedPaymentId = resolveReversedPaymentId(record);
      const lineNumber =
        resolveLineNumber(record) ||
        (reversedPaymentId > 0
          ? paymentIdToLineNumber.get(reversedPaymentId)
          : null);

      if (lineNumber) {
        addPaid(lineNumber, -amount);
      } else {
        unlinkedNetPaid -= amount;
      }
      return;
    }

    if (type !== "payment") return;

    const lineNumber = resolveLineNumber(record);
    if (lineNumber) {
      addPaid(lineNumber, amount);
      return;
    }

    unlinkedNetPaid += amount;
  });

  const lineNumbersInOrder = (lineItems || []).map(
    (line, index) => Number(line.line_number) || index + 1,
  );

  const applyUnlinkedPayments = () => {
    if (unlinkedNetPaid <= 0.009) return;

    let pool = unlinkedNetPaid;
    const candidates = lineNumbersInOrder
      .map((lineNumber, index) => {
        const line = lineItems[index];
        const lineTotal = resolvePaymentModalLineDueAmount(line);
        const paid = Math.max(paidByLine.get(lineNumber) || 0, 0);
        const remaining = Math.max(lineTotal - paid, 0);
        return { lineNumber, remaining };
      })
      .filter((entry) => entry.remaining > 0.009);

    const exactMatch = candidates.find(
      (entry) => Math.abs(entry.remaining - pool) < 0.02,
    );
    if (exactMatch) {
      addPaid(exactMatch.lineNumber, pool);
      return;
    }

    candidates
      .sort((left, right) => left.remaining - right.remaining)
      .forEach(({ lineNumber, remaining }) => {
        if (pool <= 0.009) return;
        const applied = Math.min(pool, remaining);
        addPaid(lineNumber, applied);
        pool -= applied;
      });
  };

  applyUnlinkedPayments();

  return (lineItems || [])
    .map((line, index) => {
      const lineNumber = Number(line.line_number) || index + 1;
      const lineTotal = resolvePaymentModalLineDueAmount(line);
      const paid = Math.min(
        Math.max(paidByLine.get(lineNumber) || 0, 0),
        lineTotal,
      );
      const remaining = Math.max(lineTotal - paid, 0);

      return {
        line_number: lineNumber,
        finance_invoice_item_id: line.id ?? line.finance_invoice_item_id ?? null,
        description: String(line.description || line.item_name || `Line ${lineNumber}`).trim(),
        lineTotal,
        remaining,
        commission_installment_id: line.commission_installment_id ?? null,
        bonus_installment_id: line.bonus_installment_id ?? null,
      };
    })
    .filter((row) => row.lineTotal > 0 && row.remaining > 0.009);
}

/**
 * Distribute a payment total across payable line rows (by each line's remaining due).
 * Used only in the add-payment modal — does not affect save/API logic.
 */
export function splitPaymentTotalAcrossLineRows(rows = [], totalAmount = 0) {
  const payableRows = (Array.isArray(rows) ? rows : []).filter(
    (row) => Math.max(Number(row.remaining) || 0, 0) > 0.009,
  );

  const result = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    result[row.line_number] = "";
  });

  const total = Math.max(Number(totalAmount) || 0, 0);
  if (!payableRows.length || total <= 0) {
    return result;
  }

  const sumRemaining = payableRows.reduce(
    (sum, row) => sum + Math.max(Number(row.remaining) || 0, 0),
    0,
  );
  const toAllocate = Math.min(total, sumRemaining);

  let allocated = 0;
  payableRows.forEach((row, index) => {
    const remaining = Math.max(Number(row.remaining) || 0, 0);
    let share;

    if (index === payableRows.length - 1) {
      share = Math.round((toAllocate - allocated) * 100) / 100;
    } else if (sumRemaining > 0) {
      share =
        Math.round(((toAllocate * remaining) / sumRemaining) * 100) / 100;
      allocated += share;
    } else {
      share = 0;
    }

    share = Math.min(Math.max(share, 0), remaining);
    if (share > 0) {
      result[row.line_number] = formatInvoiceAmountInput(share);
    }
  });

  return result;
}
